import type { time } from '@amcharts/amcharts5';
import { AnalystChartXAxisKind } from 'src/app/enums/analystChartXAxisKind.enum';
import { AnalystChart } from 'src/app/models/analyst-output.model';
import { format } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { DateAxisBaseInterval, msToBaseInterval, positiveDeltas, upperMedian } from './date-axis-base-interval.util';

const ISO_DATE_WITH_OFFSET_REGEX = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[+-]\d{2}:?\d{2}))?$/;
const DEFAULT_DATE_GRID_DISTANCE_PX = 60;
const AXIS_LABEL_CHAR_WIDTH_PX = 7;
const AXIS_LABEL_GAP_PX = 16;
const AXIS_LABEL_PADDING_PX = 10;
const WIDEST_LABEL_SAMPLE = new Date(2000, 11, 28, 22, 58, 58);
const DAY_MS = 24 * 60 * 60 * 1000;
const TIME_UNIT_MS: Record<time.TimeUnit, number> = {
  millisecond: 1,
  second: 1000,
  minute: 60 * 1000,
  hour: 60 * 60 * 1000,
  day: DAY_MS,
  week: 7 * DAY_MS,
  month: (365.242 / 12) * DAY_MS,
  year: 365 * DAY_MS,
};
const AXIS_TIME_UNITS: time.TimeUnit[] = ['second', 'minute', 'hour', 'day', 'week', 'month', 'year'];
const MONTH_SPACING_DAYS = { min: 28, max: 31 };
const QUARTER_SPACING_DAYS = { min: 89, max: 92 };
const AMCHARTS_AUTO_GAP_COUNT = 1.1;
const FALLBACK_BASE_INTERVAL: DateAxisBaseInterval = { timeUnit: 'hour', count: 1 };

export interface DateAxisBucketing {
  baseInterval: DateAxisBaseInterval;
  autoGapCount: number;
}

interface ZonedTimeParts {
  date: string;
  year: number;
  monthIndex: number;
  dayOfMonth: number;
  hour: number;
  minute: number;
  second: number;
}

export function parseAnalystChartDate(x: string): number | null {
  if (!ISO_DATE_WITH_OFFSET_REGEX.test(x)) {
    return null;
  }
  const parsed = Date.parse(x);
  return Number.isNaN(parsed) ? null : parsed;
}

export function deriveDateAxisBucketing(sortedTimestamps: number[], timeZone: string): DateAxisBucketing {
  const deltas = positiveDeltas(sortedTimestamps);
  const typicalDelta = upperMedian(deltas);
  if (typicalDelta === null) {
    return { baseInterval: FALLBACK_BASE_INTERVAL, autoGapCount: AMCHARTS_AUTO_GAP_COUNT };
  }

  const pointParts = sortedTimestamps.map((epoch) => zonedTimeParts(epoch, timeZone));
  const candidates = pointParts.every(isMonthStart)
    ? monthCandidates(pointParts)
    : spacingCandidates(deltas, typicalDelta);
  const typicalInterval = candidates[0];
  const baseInterval = candidates.find((interval) => !sharesBucket(pointParts, interval)) ?? candidates[candidates.length - 1];
  return {
    baseInterval,
    autoGapCount: (AMCHARTS_AUTO_GAP_COUNT * intervalMs(typicalInterval)) / intervalMs(baseInterval),
  };
}

export function renderableDateAxisUnits(sortedTimestamps: number[], baseInterval: DateAxisBaseInterval): time.TimeUnit[] {
  const span = sortedTimestamps.length === 0
    ? 0
    : sortedTimestamps[sortedTimestamps.length - 1] - sortedTimestamps[0] + intervalMs(baseInterval);
  return AXIS_TIME_UNITS.slice(AXIS_TIME_UNITS.indexOf(baseInterval.timeUnit)).filter(
    (unit, index) => index === 0 || TIME_UNIT_MS[unit] <= span,
  );
}

export function axisNeedsYear(
  sortedTimestamps: number[],
  pointTimeZone: string,
  userTimeZone: string,
  now: number = Date.now(),
): boolean {
  if (sortedTimestamps.length === 0) {
    return false;
  }
  const currentYear = formatInTimeZone(now, userTimeZone, 'yyyy');
  const year = (epoch: number): string => formatInTimeZone(epoch, pointTimeZone, 'yyyy');
  return year(sortedTimestamps[0]) !== currentYear || year(sortedTimestamps[sortedTimestamps.length - 1]) !== currentYear;
}

export function dateAxisMinGridDistance(labelFormats: string[]): number {
  return Math.max(DEFAULT_DATE_GRID_DISTANCE_PX, widestLabelTextWidth(labelFormats) + AXIS_LABEL_GAP_PX);
}

export function dateAxisEdgeInset(labelFormats: string[]): number {
  return Math.ceil((widestLabelTextWidth(labelFormats) + AXIS_LABEL_PADDING_PX) / 2);
}

function widestLabelTextWidth(labelFormats: string[]): number {
  const widestLabel = Math.max(0, ...labelFormats.map((labelFormat) => format(WIDEST_LABEL_SAMPLE, labelFormat).length));
  return widestLabel * AXIS_LABEL_CHAR_WIDTH_PX;
}

function spacingCandidates(deltas: number[], typicalDelta: number): DateAxisBaseInterval[] {
  const smallestUnit = msToBaseInterval(Math.min(...deltas), FALLBACK_BASE_INTERVAL).timeUnit;
  return [
    spacingToBaseInterval(typicalDelta),
    ...descendingBelow(deltas, typicalDelta).map(spacingToBaseInterval),
    { timeUnit: smallestUnit, count: 1 },
  ];
}

function monthCandidates(pointParts: ZonedTimeParts[]): DateAxisBaseInterval[] {
  const monthDeltas = positiveDeltas(pointParts.map((parts) => parts.year * 12 + parts.monthIndex));
  const typicalMonths = upperMedian(monthDeltas) ?? 1;
  return [typicalMonths, ...descendingBelow(monthDeltas, typicalMonths), 1].map((count): DateAxisBaseInterval => ({
    timeUnit: 'month',
    count,
  }));
}

function descendingBelow(values: number[], limit: number): number[] {
  return Array.from(new Set(values.filter((value) => value < limit))).sort((a, b) => b - a);
}

function isMonthStart(parts: ZonedTimeParts): boolean {
  return parts.dayOfMonth === 1 && parts.hour === 0 && parts.minute === 0 && parts.second === 0;
}

function spacingToBaseInterval(ms: number): DateAxisBaseInterval {
  const days = Math.round(ms / DAY_MS);
  if (days >= MONTH_SPACING_DAYS.min && days <= MONTH_SPACING_DAYS.max) {
    return { timeUnit: 'month', count: 1 };
  }
  if (days >= QUARTER_SPACING_DAYS.min && days <= QUARTER_SPACING_DAYS.max) {
    return { timeUnit: 'month', count: 3 };
  }
  return msToBaseInterval(ms, FALLBACK_BASE_INTERVAL);
}

function intervalMs(interval: DateAxisBaseInterval): number {
  return TIME_UNIT_MS[interval.timeUnit] * interval.count;
}

function zonedTimeParts(epoch: number, timeZone: string): ZonedTimeParts {
  const [date, hour, minute, second] = formatInTimeZone(epoch, timeZone, 'yyyy-MM-dd HH mm ss').split(' ');
  return {
    date,
    year: Number(date.slice(0, 4)),
    monthIndex: Number(date.slice(5, 7)) - 1,
    dayOfMonth: Number(date.slice(8, 10)),
    hour: Number(hour),
    minute: Number(minute),
    second: Number(second),
  };
}

function bucketKey(parts: ZonedTimeParts, { timeUnit, count }: DateAxisBaseInterval): string {
  const slot = (value: number): number => Math.floor(value / count);
  switch (timeUnit) {
    case 'second':
      return `${parts.date} ${parts.hour}:${parts.minute}:${slot(parts.second)}`;
    case 'minute':
      return `${parts.date} ${parts.hour}:${slot(parts.minute)}`;
    case 'hour':
      return `${parts.date} ${slot(parts.hour)}`;
    case 'month':
      return `${parts.year}-${slot(parts.monthIndex)}`;
    default:
      return parts.date;
  }
}

function sharesBucket(pointParts: ZonedTimeParts[], interval: DateAxisBaseInterval): boolean {
  return pointParts.some((parts, index) => index > 0 && bucketKey(parts, interval) === bucketKey(pointParts[index - 1], interval));
}

export function resolveAnalystChartXAxisKind(chart: AnalystChart): AnalystChartXAxisKind {
  return chart.xAxisKind === AnalystChartXAxisKind.DateTime || chart.xAxisKind === AnalystChartXAxisKind.Date
    ? chart.xAxisKind
    : AnalystChartXAxisKind.Category;
}
